/**
 * ConfirmDialog - Dialogo de confirmacion para acciones destructivas
 *
 * Diseñado para neurodivergencia:
 * - Mensaje claro sin jerga tecnica
 * - Opciones visuales distinguibles
 * - No presiona al usuario con timers
 * - Opcion de deshacer cuando es posible
 *
 * Basado en COGA: "Los cambios no deben ocurrir automaticamente sin confirmacion"
 */

import { motion, AnimatePresence } from 'framer-motion';
import { AlertTriangle, Info, HelpCircle, type LucideIcon } from 'lucide-react';
import { cn } from '@/core/utils';
import { Button } from '@/ui/primitives';
import { feedbackWarning } from '@/services/feedback';

type DialogVariant = 'danger' | 'warning' | 'info';

interface ConfirmDialogProps {
  isOpen: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  title: string;
  description: string;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: DialogVariant;
  /** Mensaje adicional tranquilizador */
  reassurance?: string;
}

const VARIANT_CONFIG: Record<DialogVariant, {
  icon: LucideIcon;
  iconColor: string;
  bgColor: string;
  borderColor: string;
  confirmButton: 'danger' | 'primary' | 'secondary';
}> = {
  danger: {
    icon: AlertTriangle,
    iconColor: 'text-rose-400',
    bgColor: 'bg-rose-900/20',
    borderColor: 'border-rose-800/50',
    confirmButton: 'danger',
  },
  warning: {
    icon: AlertTriangle,
    iconColor: 'text-amber-400',
    bgColor: 'bg-amber-900/20',
    borderColor: 'border-amber-800/50',
    confirmButton: 'primary',
  },
  info: {
    icon: Info,
    iconColor: 'text-blue-400',
    bgColor: 'bg-blue-900/20',
    borderColor: 'border-blue-800/50',
    confirmButton: 'primary',
  },
};

export function ConfirmDialog({
  isOpen,
  onConfirm,
  onCancel,
  title,
  description,
  confirmLabel = 'Confirmar',
  cancelLabel = 'Cancelar',
  variant = 'warning',
  reassurance,
}: ConfirmDialogProps) {
  const config = VARIANT_CONFIG[variant];
  const Icon = config.icon;

  const handleConfirm = () => {
    if (variant === 'danger' || variant === 'warning') {
      feedbackWarning();
    }
    onConfirm();
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center p-4"
          role="alertdialog"
          aria-modal="true"
          aria-labelledby="confirm-dialog-title"
          aria-describedby="confirm-dialog-description"
        >
          {/* Overlay */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={onCancel}
            aria-hidden="true"
          />

          {/* Dialog */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 10 }}
            transition={{ duration: 0.2, ease: 'easeOut' }}
            className={cn(
              'relative w-full max-w-sm bg-slate-900 border rounded-xl shadow-2xl overflow-hidden',
              config.borderColor
            )}
          >
            {/* Header con icono */}
            <div className={cn('p-4', config.bgColor)}>
              <div className="flex items-start gap-4">
                <div className={cn('p-2 rounded-full', config.bgColor)}>
                  <Icon className={cn('w-6 h-6', config.iconColor)} />
                </div>
                <div className="flex-1 pt-1">
                  <h2
                    id="confirm-dialog-title"
                    className="text-lg font-semibold text-slate-100"
                  >
                    {title}
                  </h2>
                  <p
                    id="confirm-dialog-description"
                    className="mt-1 text-sm text-slate-400"
                  >
                    {description}
                  </p>
                </div>
              </div>
            </div>

            {/* Mensaje tranquilizador */}
            {reassurance && (
              <div className="px-4 py-3 border-t border-slate-800/50 bg-slate-800/30">
                <div className="flex items-start gap-2">
                  <HelpCircle className="w-4 h-4 text-slate-500 flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-slate-500">
                    {reassurance}
                  </p>
                </div>
              </div>
            )}

            {/* Acciones */}
            <div className="flex gap-3 p-4 bg-slate-900">
              <Button
                variant="ghost"
                size="md"
                onClick={onCancel}
                className="flex-1"
              >
                {cancelLabel}
              </Button>
              <Button
                variant={config.confirmButton}
                size="md"
                onClick={handleConfirm}
                className="flex-1"
              >
                {confirmLabel}
              </Button>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}

/**
 * Hook para manejar estado de confirmacion
 */
import { useState, useCallback } from 'react';

interface UseConfirmOptions {
  onConfirm: () => void;
  title: string;
  description: string;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: DialogVariant;
  reassurance?: string;
}

interface UseConfirmReturn {
  /** Abre el dialogo de confirmacion */
  requestConfirm: () => void;
  /** Props para pasar al componente ConfirmDialog */
  dialogProps: ConfirmDialogProps;
}

export function useConfirm(options: UseConfirmOptions): UseConfirmReturn {
  const [isOpen, setIsOpen] = useState(false);

  const requestConfirm = useCallback(() => {
    setIsOpen(true);
  }, []);

  const handleConfirm = useCallback(() => {
    setIsOpen(false);
    options.onConfirm();
  }, [options]);

  const handleCancel = useCallback(() => {
    setIsOpen(false);
  }, []);

  const dialogProps: ConfirmDialogProps = {
    isOpen,
    onConfirm: handleConfirm,
    onCancel: handleCancel,
    title: options.title,
    description: options.description,
  };

  // Solo agregar propiedades opcionales si estan definidas
  if (options.confirmLabel !== undefined) {
    dialogProps.confirmLabel = options.confirmLabel;
  }
  if (options.cancelLabel !== undefined) {
    dialogProps.cancelLabel = options.cancelLabel;
  }
  if (options.variant !== undefined) {
    dialogProps.variant = options.variant;
  }
  if (options.reassurance !== undefined) {
    dialogProps.reassurance = options.reassurance;
  }

  return {
    requestConfirm,
    dialogProps,
  };
}
